/* Warm every public product and collection progressively. Complete-page
 * prerenders remain in ts-navigation-prefetch.js, within browser limits.
 * HTML is inspected only: it is never replayed as a page or stored as prices. */
(() => {
  const loader = document.currentScript;
  const root = new URL(loader.dataset.storeRoot || '/', location.href).pathname.replace(/\/$/, '') + '/';
  const storageKey = 'ts-catalog-preload:1:' + root + ':' + loader.src;
  const lifetime = 24 * 60 * 60 * 1000;
  const pages = new Set();
  let likelyPage;
  let activeJob;
  let started = false;
  let foregroundUntil = 0;
  const maps = new Set();
  const completedMaps = new Set();
  const completedAssets = new Set();
  const failures = new Map();
  let completed = new Set();
  let expires = Date.now() + lifetime;
  let manifestReady = false;
  let currentPage;
  let resources = [];
  let controller;
  let busy = false;
  let scheduled = false;
  let lastInput = performance.now();
  let retryAfter = 0;
  let dirty = false;
  let saveTimer;

  function allowed() {
    const connection = navigator.connection;
    return !document.prerendering && document.visibilityState === 'visible' &&
      navigator.onLine !== false && !connection?.saveData &&
      !['slow-2g', '2g', '3g'].includes(connection?.effectiveType);
  }

  function pageURL(value) {
    let url;
    try { url = new URL(value, location.origin + root); } catch (_) { return; }
    if (url.origin !== location.origin || !url.pathname.startsWith(root) || url.username || url.password) return;
    const path = url.pathname.slice(root.length).replace(/\/$/, '');
    const product = path.match(/^(?:collections\/[\w%-]+\/)?products\/([\w%-]+)$/);
    if (product && [...url.searchParams.keys()].every(key => ['variant','_pos','_sid','_ss','_psq','_v','pr_prod_strat','pr_rec_id','pr_rec_pid','pr_ref_pid','pr_seq'].includes(key))) {
      return location.origin + root + 'products/' + product[1];
    }
    if (/^collections(?:\/[\w%-]+)?$/.test(path) && !url.search) return location.origin + root + path;
    if (path === 'collections' && /^\?view=ts-(?:underground|aerial|fiber-optics|copper|racks-cabinets|tools-equipment)$/.test(url.search)) {
      return location.origin + root + path + url.search;
    }
  }

  function sitemapURL(value) {
    let url;
    try { url = new URL(value, location.origin + root); } catch (_) { return; }
    if (url.origin !== location.origin || url.username || url.password || !url.pathname.startsWith(root)) return;
    const path = url.pathname.slice(root.length);
    if (path === 'sitemap.xml' || /^sitemap_(?:products|collections)_\d+\.xml$/.test(path)) return url.href;
  }

  function assetURL(value, base, image = false) {
    if (!value) return;
    let url;
    try { url = new URL(value, base); } catch (_) { return; }
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return;
    const own = url.origin === location.origin && /^\/cdn\/shop\/(?:t\/\d+\/assets|files|products)\//.test(url.pathname);
    const cdn = url.origin === 'https://cdn.shopify.com' && /^\/s\/files\/\d+\/\d+\/\d+(?:\/\d+)?\/(?:t\/\d+\/assets|files|products)\//.test(url.pathname);
    if (!(own || cdn)) return;
    const extension = image ? /\.(?:avif|webp|png|jpe?g|gif|svg)$/i : /\.(?:css|js|woff2?)$/i;
    if (extension.test(url.pathname)) return url.href;
  }

  function remember() {
    if (!dirty) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify({expires, manifestReady, pages: [...pages], completed: [...completed]}));
      dirty = false;
    } catch (_) { /* Storage may be disabled or full; warming still works. */ }
  }

  function changed() {
    dirty = true;
    if (saveTimer) return;
    saveTimer = setTimeout(() => { saveTimer = undefined; remember(); }, 3000);
  }

  function restore() {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey));
      if (!saved || saved.expires <= Date.now() || !Array.isArray(saved.pages) || !Array.isArray(saved.completed)) return;
      expires = saved.expires;
      saved.pages.forEach(value => { const url = pageURL(value); if (url) pages.add(url); });
      completed = new Set(saved.completed.map(pageURL).filter(Boolean));
      manifestReady = saved.manifestReady === true;
    } catch (_) { /* Storage is optional. */ }
  }

  function register(link) {
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = pageURL(link.href);
    if (!url) return;
    if (!pages.has(url)) { pages.add(url); changed(); }
  }

  function scan() {
    document.querySelectorAll('a[href]').forEach(link => register(link));
    schedule();
  }

  function parseSitemap(text) {
    const xml = new DOMParser().parseFromString(text, 'application/xml');
    if (xml.querySelector('parsererror')) throw new Error('Invalid sitemap');
    const type = xml.documentElement.localName;
    if (type !== 'sitemapindex' && type !== 'urlset') throw new Error('Unexpected sitemap');
    for (const entry of xml.documentElement.children) {
      const loc = [...entry.children].find(child => child.localName === 'loc');
      if (!loc) continue;
      const url = type === 'sitemapindex' ? sitemapURL(loc.textContent.trim()) : pageURL(loc.textContent.trim());
      if (url) (type === 'sitemapindex' ? maps : pages).add(url);
    }
    changed();
  }

  function pageResources(html, base) {
    // Template contents are inert: no apps, tracking, scripts or frames run.
    const template = document.createElement('template');
    template.innerHTML = html;
    const found = new Map();
    for (const node of template.content.querySelectorAll('link[rel="stylesheet"][href], script[src], link[rel="preload"][as="font"][href]')) {
      const url = assetURL(node.getAttribute('src') || node.getAttribute('href'), base);
      if (url) found.set(url, {url});
    }
    for (const node of template.content.querySelectorAll('img[src], img[srcset]')) {
      const url = assetURL(node.getAttribute('src'), base, true);
      const candidates = (node.getAttribute('srcset') || '').split(',').map(candidate => {
        const [src, size = ''] = candidate.trim().split(/\s+/);
        const safe = assetURL(src, base, true);
        return safe && /^(?:\d+w|\d+(?:\.\d+)?x)?$/.test(size) ? (safe + ' ' + size).trim() : '';
      }).filter(Boolean).join(', ');
      if (!url && !candidates) continue;
      const item = {url, srcset: candidates, sizes: node.getAttribute('sizes') || '100vw', image: true};
      const key = candidates ? candidates + '|' + item.sizes : url;
      found.set(key, {...item, key});
    }
    return [...found.values()].map(item => ({...item, key: item.key || item.url})).filter(item => !completedAssets.has(item.key));
  }

  async function download(url, signal, html = false) {
    const response = await fetch(url, {
      method: 'GET', credentials: 'same-origin', cache: 'default', priority: 'low',
      redirect: 'error', mode: html || new URL(url).origin === location.origin ? 'same-origin' : 'cors', signal
    });
    if (!response.ok) {
      if (response.status === 429 || response.status >= 500) {
        const header = response.headers.get('Retry-After');
        const seconds = Number(header);
        retryAfter = Date.now() + Math.max(30000, Number.isFinite(seconds) ? seconds * 1000 : (Date.parse(header) - Date.now()) || 0);
      }
      throw new Error('Background request failed');
    }
    if (html && !response.headers.get('Content-Type')?.includes('text/html')) throw new Error('Not a storefront page');
    return response;
  }

  function warmImage(item, signal) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const finish = error => {
        img.onload = img.onerror = null;
        signal.removeEventListener('abort', abort);
        if (error) reject(error); else resolve();
      };
      const abort = () => {
        img.removeAttribute('srcset'); img.removeAttribute('src');
        finish(new DOMException('Paused', 'AbortError'));
      };
      img.onload = () => finish();
      img.onerror = () => finish(new Error('Image unavailable'));
      img.fetchPriority = 'low';
      img.decoding = 'async';
      signal.addEventListener('abort', abort, {once: true});
      if (item.srcset) { img.sizes = item.sizes; img.srcset = item.srcset; }
      if (item.url) img.src = item.url;
    });
  }

  function nextJob() {
    if (currentPage && !resources.length) {
      completed.add(currentPage); currentPage = undefined; changed();
    }
    if (likelyPage && completed.has(likelyPage)) likelyPage = undefined;
    if (likelyPage && currentPage !== likelyPage) {
      // The interrupted page stays in the catalog queue. Any finished assets
      // are already deduplicated when that page is visited again.
      resources = []; currentPage = undefined;
      return {type: 'page', url: likelyPage, key: likelyPage};
    }
    if (resources.length) return {type: 'asset', item: resources[0], key: resources[0].key};
    // Discover the entire catalog, including products absent from navigation.
    for (const url of maps) if (!completedMaps.has(url)) return {type: 'map', url, key: url};
    if (!manifestReady) { manifestReady = true; changed(); }
    for (const url of pages) if (!completed.has(url)) return {type: 'page', url, key: url};
  }

  async function pump() {
    scheduled = false;
    if (!allowed() || busy) return;
    if (Date.now() < foregroundUntil || Date.now() < retryAfter || (!likelyPage && performance.now() - lastInput < 1200)) { schedule(); return; }
    const job = nextJob();
    if (!job) { remember(); return; }
    busy = true; activeJob = job;
    controller = new AbortController();
    const signal = controller.signal;
    let timedOut = false;
    const timeout = setTimeout(() => { timedOut = true; controller?.abort(); }, 20000);
    try {
      if (job.type === 'map') {
        parseSitemap(await (await download(job.url, signal)).text());
        completedMaps.add(job.url);
      } else if (job.type === 'page') {
        resources = pageResources(await (await download(job.url, signal, true)).text(), job.url);
        currentPage = job.url;
      } else {
        if (job.item.image) await warmImage(job.item, signal);
        else await (await download(job.item.url, signal)).arrayBuffer();
        completedAssets.add(job.key);
        resources.shift();
      }
      failures.delete(job.key);
    } catch (_) {
      if (!signal.aborted || timedOut) {
        const count = (failures.get(job.key) || 0) + 1;
        failures.set(job.key, count);
        if (count >= 2) {
          if (job.type === 'asset') resources.shift();
          else if (job.type === 'map') {
            // Do not persist an incomplete inventory; retry discovery next visit.
            maps.delete(job.url);
            manifestReady = true;
            expires = Date.now();
          } else { completed.add(job.url); changed(); }
        } else retryAfter = Math.max(retryAfter, Date.now() + 3000);
      }
    } finally {
      clearTimeout(timeout);
      controller = undefined; activeJob = undefined; busy = false;
      schedule();
    }
  }

  function schedule() {
    if (!started || scheduled || busy || !allowed()) return;
    scheduled = true;
    setTimeout(() => {
      if (window.requestIdleCallback) window.requestIdleCallback(pump);
      else pump();
    }, Math.min(30000, Math.max(150, Math.max(retryAfter, foregroundUntil) - Date.now())));
  }

  function pause() { controller?.abort(); remember(); }

  document.addEventListener('ts:navigation-priority', event => {
    if (!allowed() || window.Shopify?.designMode) return;
    if (event.detail?.handled) {
      // Native navigation or a Shopify section is already fetching this destination.
      likelyPage = undefined;
      foregroundUntil = Date.now() + 2000;
      controller?.abort();
    } else {
      const url = pageURL(event.detail?.url);
      if (!url || completed.has(url) || url === pageURL(location.href)) return;
      likelyPage = url;
      if (started && !pages.has(url)) { pages.add(url); changed(); }
      if (activeJob && currentPage !== url && activeJob.url !== url) controller?.abort();
    }
    schedule();
  });

  function start() {
    if (window.Shopify?.designMode) return;
    restore(); started = true;
    if (likelyPage && !pages.has(likelyPage)) { pages.add(likelyPage); changed(); }
    if (!manifestReady) maps.add(location.origin + root + 'sitemap.xml');
    const here = pageURL(location.href);
    if (here) completed.add(here);
    scan();
    for (const event of ['pointerdown', 'keydown', 'scroll']) {
      document.addEventListener(event, () => {
        lastInput = performance.now();
        pause();
        schedule();
      }, {passive: true});
    }
    document.addEventListener('visibilitychange', () => { if (!allowed()) pause(); else schedule(); });
    window.addEventListener('pagehide', pause);
    window.addEventListener('pageshow', schedule);
    window.addEventListener('offline', pause);
    window.addEventListener('online', schedule);
    navigator.connection?.addEventListener?.('change', () => { if (!allowed()) pause(); else schedule(); });
  }

  function ready() {
    if (document.prerendering) document.addEventListener('prerenderingchange', start, {once: true});
    else start();
  }
  if (document.readyState === 'complete') ready();
  else window.addEventListener('load', ready, {once: true});
})();
